/**
 * Permit Inspection Lifecycle — Phase 1E
 * ─────────────────────────────────────────────────────────────────────
 * Closes the operational gap after permit Approval: rough/final
 * inspections, failures, corrections, reinspections, and readiness to
 * close the permit.
 *
 * Invariants:
 * 1. Optional until first inspection exists. `permitDetail.inspectionLifecycle`
 *    is only present after a coordinator opens one (or once an item is added).
 * 2. Append-safe: adding an inspection never disturbs prior rows.
 *    Per-row mutations (result, owner, notes) flow through `updateInspection`
 *    / `recordInspectionResult` only.
 * 3. Pure derivation only. `resolveInspectionReadiness` never mutates the
 *    job, never advances permit stage, never auto-closes anything.
 * 4. NO automation: no scheduling engine, no municipality calls, no
 *    notifications, no AI, no auto-close. Coordination only.
 */
import { z } from "zod";
import type { Job, PermitDetail } from "@/data/types";

export type InspectionResult = "passed" | "failed";
export type InspectionLifecycleStatus = "not_started" | "active" | "complete";

export interface PermitInspection {
  id: string;
  inspectionType: string;
  scheduledDate?: string;
  completedDate?: string;
  result?: InspectionResult;
  failureReason?: string;
  correctionRequired?: boolean;
  reinspectionRequired?: boolean;
  assignedOwner?: string;
  notes?: string;
}

export interface InspectionLifecycle {
  status: InspectionLifecycleStatus;
  inspections: PermitInspection[];
}

// ── Validation ──────────────────────────────────────────────────────

const MAX_FREEFORM = 500;
const MAX_SHORT = 160;

export const inspectionInputSchema = z.object({
  inspectionType: z.string().trim().min(1, "Inspection type is required").max(MAX_SHORT),
  scheduledDate: z
    .string()
    .datetime({ offset: true, message: "Scheduled date must be a full ISO datetime" })
    .optional()
    .or(z.literal("").transform(() => undefined)),
  assignedOwner: z.string().trim().max(MAX_SHORT).optional().or(z.literal("").transform(() => undefined)),
  notes: z.string().trim().max(MAX_FREEFORM).optional().or(z.literal("").transform(() => undefined)),
});

export type InspectionInput = z.infer<typeof inspectionInputSchema>;

export const inspectionResultInputSchema = z
  .object({
    result: z.enum(["passed", "failed"]),
    failureReason: z.string().trim().max(MAX_FREEFORM).optional().or(z.literal("").transform(() => undefined)),
    correctionRequired: z.boolean().optional(),
    reinspectionRequired: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.result === "failed" && (!val.failureReason || val.failureReason.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["failureReason"],
        message: "Failure reason is required when marking an inspection failed",
      });
    }
  });

export type InspectionResultInput = z.infer<typeof inspectionResultInputSchema>;

// ── Suggested types (freeform allowed; this is just helper data) ────

export const SUGGESTED_INSPECTION_TYPES = [
  "Rough Plumbing",
  "Rough Electrical",
  "Rough Mechanical",
  "Framing",
  "Insulation",
  "Final Building",
  "Final Plumbing",
  "Final Electrical",
  "Final Mechanical",
  "Fire",
] as const;

// ── Read accessors ──────────────────────────────────────────────────

export function getInspectionLifecycle(job: Job | null | undefined): InspectionLifecycle | null {
  return job?.permitDetail?.inspectionLifecycle ?? null;
}

export function hasInspectionLifecycle(job: Job | null | undefined): boolean {
  return !!job?.permitDetail?.inspectionLifecycle;
}

// ── Derivation ──────────────────────────────────────────────────────

export type InspectionTone = "neutral" | "amber" | "red" | "green";

export interface InspectionReadiness {
  status: InspectionLifecycleStatus;
  tone: InspectionTone;
  totalInspections: number;
  completedCount: number;
  passedCount: number;
  failedCount: number;
  /** Failed inspections that haven't been superseded by a later passed reinspection of the same type. */
  unresolvedFailures: PermitInspection[];
  /** UI-ready label e.g. "2 / 4 inspections complete". */
  progressLabel: string;
  /** UI-ready badge e.g. "Active", "Ready to Close Permit". */
  badgeLabel: string;
}

/**
 * Derive lifecycle status from the inspection list.
 *  - 0 inspections → not_started
 *  - all completed AND none failed → complete
 *  - any failure that hasn't been superseded by a later passed inspection
 *    of the same type → active + red tone
 *  - otherwise (some incomplete) → active + amber
 */
export function resolveInspectionReadiness(
  lifecycle: InspectionLifecycle | null | undefined,
): InspectionReadiness {
  const inspections = lifecycle?.inspections ?? [];
  const total = inspections.length;
  const completed = inspections.filter((i) => !!i.completedDate || !!i.result);
  const passed = inspections.filter((i) => i.result === "passed");
  const failed = inspections.filter((i) => i.result === "failed");

  // An unresolved failure is a failed inspection of type T with no
  // later passed inspection of the same type. "Later" = appears later
  // in the inspection array (append order is the source of truth) OR
  // has a strictly greater completedDate timestamp.
  const unresolvedFailures = failed.filter((f) => {
    const fIdx = inspections.indexOf(f);
    return !inspections.some((later, lIdx) => {
      if (later.id === f.id) return false;
      if (later.result !== "passed") return false;
      if (later.inspectionType.trim().toLowerCase() !== f.inspectionType.trim().toLowerCase()) return false;
      const dateLater = (later.completedDate ?? "") > (f.completedDate ?? "");
      return lIdx > fIdx || dateLater;
    });
  });

  if (total === 0) {
    return {
      status: "not_started",
      tone: "neutral",
      totalInspections: 0,
      completedCount: 0,
      passedCount: 0,
      failedCount: 0,
      unresolvedFailures: [],
      progressLabel: "0 / 0 inspections complete",
      badgeLabel: "Not Started",
    };
  }

  const allComplete = completed.length === total;
  const noUnresolvedFailures = unresolvedFailures.length === 0;

  if (allComplete && noUnresolvedFailures) {
    return {
      status: "complete",
      tone: "green",
      totalInspections: total,
      completedCount: completed.length,
      passedCount: passed.length,
      failedCount: failed.length,
      unresolvedFailures: [],
      progressLabel: `${completed.length} / ${total} inspections complete`,
      badgeLabel: "Ready to Close Permit",
    };
  }

  const tone: InspectionTone = unresolvedFailures.length > 0 ? "red" : "amber";
  const badge =
    unresolvedFailures.length > 0
      ? `Inspection Failure · ${unresolvedFailures[0].inspectionType} failed`
      : "Active";

  return {
    status: "active",
    tone,
    totalInspections: total,
    completedCount: completed.length,
    passedCount: passed.length,
    failedCount: failed.length,
    unresolvedFailures,
    progressLabel: `${completed.length} / ${total} inspections complete`,
    badgeLabel: badge,
  };
}

// ── Mutators (sole writers) ─────────────────────────────────────────

function withPermitDetail(job: Job, mutate: (pd: PermitDetail) => PermitDetail): Job {
  if (!job.permitDetail) return job;
  return { ...job, permitDetail: mutate(job.permitDetail) };
}

function makeInspectionId(): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `ins-${Date.now()}-${rand}`;
}

function ensureLifecycle(pd: PermitDetail): InspectionLifecycle {
  return pd.inspectionLifecycle ?? { status: "not_started", inspections: [] };
}

/**
 * Reproject lifecycle status from inspections after any mutation.
 * Persisted `status` is a cache of the derivation — UI surfaces should
 * still call `resolveInspectionReadiness` for tone + labels.
 */
function projectStatus(inspections: PermitInspection[]): InspectionLifecycleStatus {
  if (inspections.length === 0) return "not_started";
  const allComplete = inspections.every((i) => !!i.completedDate || !!i.result);
  if (!allComplete) return "active";
  // Mirror resolveInspectionReadiness: complete iff no unresolved failures.
  return resolveInspectionReadiness({ status: "active", inspections }).status;
}

export function addInspection(job: Job, input: InspectionInput): Job {
  const parsed = inspectionInputSchema.parse(input);
  return withPermitDetail(job, (pd) => {
    const current = ensureLifecycle(pd);
    const next: PermitInspection = {
      id: makeInspectionId(),
      inspectionType: parsed.inspectionType,
      scheduledDate: parsed.scheduledDate,
      assignedOwner: parsed.assignedOwner,
      notes: parsed.notes,
    };
    const inspections = [...current.inspections, next];
    return {
      ...pd,
      inspectionLifecycle: { status: projectStatus(inspections), inspections },
    };
  });
}

/**
 * Update non-result fields on a single inspection (owner, notes,
 * scheduledDate). Result fields are managed via `recordInspectionResult`.
 */
export function updateInspection(
  job: Job,
  inspectionId: string,
  patch: Partial<Pick<PermitInspection, "inspectionType" | "scheduledDate" | "assignedOwner" | "notes">>,
): Job {
  return withPermitDetail(job, (pd) => {
    if (!pd.inspectionLifecycle) return pd;
    const inspections = pd.inspectionLifecycle.inspections.map((i) =>
      i.id === inspectionId ? { ...i, ...patch } : i,
    );
    return {
      ...pd,
      inspectionLifecycle: { status: projectStatus(inspections), inspections },
    };
  });
}

/**
 * Record a pass/fail result on an inspection. Stamps `completedDate`
 * (now ISO). Failure requires a `failureReason`. Never mutates permit
 * stage; never auto-creates a reinspection row — coordinators add a
 * follow-up inspection explicitly.
 */
export function recordInspectionResult(
  job: Job,
  inspectionId: string,
  input: InspectionResultInput,
): Job {
  const parsed = inspectionResultInputSchema.parse(input);
  return withPermitDetail(job, (pd) => {
    if (!pd.inspectionLifecycle) return pd;
    const completedAt = new Date().toISOString();
    const inspections = pd.inspectionLifecycle.inspections.map((i) => {
      if (i.id !== inspectionId) return i;
      if (parsed.result === "passed") {
        // Clear any prior failure metadata if re-recording.
        const { failureReason: _fr, correctionRequired: _cr, reinspectionRequired: _rr, ...rest } = i;
        void _fr; void _cr; void _rr;
        return {
          ...rest,
          result: "passed" as const,
          completedDate: completedAt,
        };
      }
      return {
        ...i,
        result: "failed" as const,
        completedDate: completedAt,
        failureReason: parsed.failureReason,
        correctionRequired: parsed.correctionRequired ?? false,
        reinspectionRequired: parsed.reinspectionRequired ?? false,
      };
    });
    return {
      ...pd,
      inspectionLifecycle: { status: projectStatus(inspections), inspections },
    };
  });
}

/**
 * Clear a recorded result (re-open an inspection). Removes
 * completedDate + result + failure metadata. Coordinator workflow only.
 */
export function clearInspectionResult(job: Job, inspectionId: string): Job {
  return withPermitDetail(job, (pd) => {
    if (!pd.inspectionLifecycle) return pd;
    const inspections = pd.inspectionLifecycle.inspections.map((i) => {
      if (i.id !== inspectionId) return i;
      const { result: _r, completedDate: _c, failureReason: _fr, correctionRequired: _cr, reinspectionRequired: _rr, ...rest } = i;
      void _r; void _c; void _fr; void _cr; void _rr;
      return rest;
    });
    return {
      ...pd,
      inspectionLifecycle: { status: projectStatus(inspections), inspections },
    };
  });
}
